
import { Attendee } from '@/types/attendee';
import { SortField, SortDirection } from './SortableTableHeader';

// Helper function to remove Vietnamese accents
export const removeVietnameseAccents = (str: string): string => {
  if (!str) return '';
  
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');
};

// Performs accent-insensitive search
export const accentInsensitiveSearch = (text: string, query: string): boolean => {
  if (!text || !query) return false;
  
  const normalizedText = removeVietnameseAccents(text).toLowerCase();
  const normalizedQuery = removeVietnameseAccents(query).toLowerCase();
  
  return normalizedText.includes(normalizedQuery) || text.toLowerCase().includes(query.toLowerCase());
};

// Filter attendees based on search query
export const filterAttendees = (attendees: Attendee[], searchQuery: string): Attendee[] => {
  if (!searchQuery.trim()) return attendees;
  
  const query = searchQuery.toLowerCase();
  return attendees.filter(attendee => {
    // Search in both original text and normalized (accent-removed) text
    return (
      accentInsensitiveSearch(attendee.name, query) ||
      accentInsensitiveSearch(attendee.email || '', query) ||
      accentInsensitiveSearch(attendee.phone || '', query) ||
      accentInsensitiveSearch(attendee.class, query) ||
      accentInsensitiveSearch(attendee.code, query) ||
      (attendee.t_shirt_size && accentInsensitiveSearch(attendee.t_shirt_size, query))
    );
  });
};

// Sort attendees based on sort field and direction
export const sortAttendees = (
  attendees: Attendee[], 
  sortField: SortField, 
  sortDirection: SortDirection
): Attendee[] => {
  const sortedAttendees = [...attendees];
  
  sortedAttendees.sort((a, b) => {
    let comparison = 0;
    
    switch (sortField) {
      case 'name':
        comparison = a.name.localeCompare(b.name, 'vi');
        break;
      case 'class':
        comparison = a.class.localeCompare(b.class, 'vi');
        break;
      case 'contribution':
        comparison = (a.contribution || 0) - (b.contribution || 0);
        break;
      case 'registeredAt':
        comparison = new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime();
        break;
      case 't_shirt_size':
        // Get actual size for comparison (without prefix)
        const aSize = a.t_shirt_size || 'Nam_L';
        const bSize = b.t_shirt_size || 'Nam_L';
        
        // Extract just the size part (S, M, L, XL, etc.) for primary sorting
        const aSizeOnly = aSize.includes('_') ? aSize.split('_')[1] : aSize;
        const bSizeOnly = bSize.includes('_') ? bSize.split('_')[1] : bSize;
        
        // Convert sizes to numeric values for proper sorting
        const sizeValues: { [key: string]: number } = { 
          XS: 1, 
          S: 2, 
          M: 3, 
          L: 4, 
          XL: 5, 
          '2XL': 6, 
          '3XL': 7, 
          '4XL': 8 
        };
        
        // Handle different ways 2XL, 3XL might be stored
        const getNormalizedSize = (size: string): string => {
          if (['XXL', '2XL'].includes(size)) return '2XL';
          if (['XXXL', '3XL'].includes(size)) return '3XL';
          if (['XXXXL', '4XL'].includes(size)) return '4XL';
          return size;
        };
        
        const normalizedASizeOnly = getNormalizedSize(aSizeOnly);
        const normalizedBSizeOnly = getNormalizedSize(bSizeOnly);
        
        const aSizeValue = sizeValues[normalizedASizeOnly] || 0;
        const bSizeValue = sizeValues[normalizedBSizeOnly] || 0;
        
        // Primary sort by size value
        if (aSizeValue !== bSizeValue) {
          comparison = aSizeValue - bSizeValue;
        } else {
          // Secondary sort by gender if sizes are the same
          const aIsNam = aSize.startsWith('Nam_');
          const bIsNam = bSize.startsWith('Nam_');
          comparison = aIsNam === bIsNam ? 0 : aIsNam ? -1 : 1;
        }
        break;
      default:
        comparison = new Date(a.registeredAt).getTime() - new Date(b.registeredAt).getTime();
    }
    
    // Apply sort direction
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  
  return sortedAttendees;
};
